import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ImagePlus } from 'lucide-react';
import GenerationShell from '../components/GenerationShell';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/apiClient';
import generationApi, { errorMessage, newCommandKey } from '../lib/generationApi';
import { clearExampleBrief, readExampleBrief } from '../lib/exampleBrief';

const inputClass = 'studio-input';

export default function Create() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [brief, setBrief] = useState(() => (searchParams.get('brief') || readExampleBrief()).slice(0, 8000));
  useEffect(() => { clearExampleBrief(); }, []);
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(15);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const availableCredits = user?.total_credits ?? user?.credits ?? 0;
  const requiredCredits = duration * 2;
  const missingCredits = Math.max(0, requiredCredits - availableCredits);
  const keyRef = useRef(null);
  const storageKey = `adfini-submission:${user?._id || user?.id || user?.email}`;
  useEffect(() => {
    const savedKey = sessionStorage.getItem(storageKey);
    if (!savedKey) return undefined;
    let alive = true;
    generationApi.history().then(items => {
      const saved = items.find(item => item.submission_key === savedKey);
      if (alive && saved) { sessionStorage.removeItem(storageKey); navigate(`/runs/${saved.id}`); }
    }).catch(() => {});
    return () => { alive = false; };
  }, [storageKey, navigate]);
  useEffect(() => { apiClient.get('/api/projects').then(({ data }) => setProjects(data)).catch(() => {}); }, []);
  useEffect(() => {
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [files]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (files.length < 1 || files.length > 6 || files.some(f => f.size > 15_000_000) || files.reduce((sum, f) => sum + f.size, 0) > 30_000_000) {
      setError('Choose 1–6 photos, at most 15 MB each and 30 MB combined.'); return;
    }
    keyRef.current = keyRef.current || sessionStorage.getItem(storageKey) || newCommandKey();
    sessionStorage.setItem(storageKey, keyRef.current);
    const form = new FormData();
    form.append('brief', brief); form.append('duration_seconds', String(duration)); form.append('title', title);
    if (project) form.append('project_id', project);
    files.forEach(file => form.append('images', file));
    setBusy(true);
    try {
      const response = await generationApi.create(form, keyRef.current);
      sessionStorage.removeItem(storageKey);
      await refreshUser();
      navigate(`/runs/${response.job.id}`);
    } catch (err) {
      setError(errorMessage(err));
      // Retain the exact key after uncertain delivery; do not silently buy another run.
      const status = err?.response?.status;
      setUncertain(!status || status >= 500 || status === 409);
      if (status && status < 500 && status !== 409) { sessionStorage.removeItem(storageKey); keyRef.current = null; }
    } finally { setBusy(false); }
  }

  return <GenerationShell><div>
    <Link to="/dashboard" className="studio-back-link"><ArrowLeft size={15} aria-hidden />Your workspace</Link>
    <h1>Let’s put your product in motion.</h1>
    <p className="studio-page-description">Bring the photos. Set the direction. We’ll plan, create, and assemble your video.</p>
    <div className="studio-form-layout"><form onSubmit={submit} className="studio-form">
      <fieldset disabled={busy || uncertain}>
        <div><label htmlFor="product-photos" className="studio-field-label">Product photos</label><div className="studio-upload"><ImagePlus size={27} aria-hidden /><p>Start with a clear view of your product.</p><p className="studio-form-help" id="photo-help">1–6 JPEG, PNG or WebP photos. Up to 15 MB and 40 megapixels each; 30 MB total.</p><input id="product-photos" aria-describedby="photo-help" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => setFiles(Array.from(e.target.files || []))} />
          {previews.length > 0 && <div className="studio-photo-previews">{previews.map((url, i) => <img key={url} src={url} alt={`Product photo ${i + 1}`} />)}</div>}
        </div></div>
        <label><span className="studio-field-label">Tell us about your product</span><textarea required minLength={5} maxLength={8000} rows={6} value={brief} onChange={e => setBrief(e.target.value)} className={inputClass} placeholder="What is the product, who is it for, and what should people remember? Include key features and the feeling you want." /><span className="studio-form-help studio-character-count block">{brief.length.toLocaleString()}/8,000</span></label>
        <fieldset><legend className="studio-field-label">Video length</legend><div className="studio-duration-options">{[15, 30, 45].map(seconds => <button key={seconds} type="button" aria-pressed={duration === seconds} onClick={() => setDuration(seconds)}><span>{seconds} seconds</span><small>{seconds * 2} credits</small></button>)}</div><p className="studio-form-help">Vertical video. Includes the standard image correction attempt.</p></fieldset>
        <label><span className="studio-field-label">Video title <span>(optional)</span></span><input value={title} maxLength={200} onChange={e => setTitle(e.target.value)} className={inputClass} placeholder="Your next campaign" /></label>
        {projects.length > 0 && <label><span className="studio-field-label">Project <span>(optional)</span></span><select value={project} onChange={e => setProject(e.target.value)} className={inputClass}><option value="">No project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}</select></label>}
      </fieldset>
      {error && <p role="alert" className="studio-alert studio-alert-error mt-5">{error}</p>}
      {uncertain && <p className="studio-alert mt-4">Submission could not be confirmed. Retry with these same inputs, or check <Link to="/dashboard" className="underline">your workspace</Link> before starting another video.</p>}
      {!uncertain && missingCredits > 0 && <p id="credit-shortfall" role="status" className="studio-alert mt-5">You need {missingCredits} more credits for this {duration}-second video. <Link to="/billing" className="underline">Add credits</Link> to start your production.</p>}
      <div className="studio-form-footer"><p>Available: {availableCredits} credits · <Link to="/billing">Add credits</Link></p><button disabled={busy || (!uncertain && missingCredits > 0)} aria-describedby={!uncertain && missingCredits > 0 ? 'credit-shortfall' : undefined} type="submit" className="studio-button studio-button-primary">{busy ? 'Submitting…' : uncertain ? 'Retry same submission' : `Create video · ${requiredCredits} credits`}<ArrowRight size={16} aria-hidden /></button></div>
    </form><aside className="studio-form-summary"><h2>From here, we take it forward.</h2><ol><li>Plan the creative direction</li><li>Create and inspect the frames</li><li>Generate and assemble your film</li></ol><p>You’ll see progress in your workspace. We’ll only ask for an image decision if it still fails inspection after its correction.</p><p>Your photos and brief shape this production. No category selection required.</p></aside></div>
  </div></GenerationShell>;
}
